// frontend/src/components/engineer/modals/ChangeLogModal.tsx
import React, { useState } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  XMarkIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { ChangeLog, changeLogService } from '../../../services/changeLog.service';
import { AuthUser } from '../../../services/auth.service';
import styles from './ChangeLogModal.module.css';

interface ChangeLogModalProps {
  changeLog: ChangeLog;
  user: AuthUser;
  onAcknowledge: () => void;
}

const ChangeLogModal: React.FC<ChangeLogModalProps> = ({
  changeLog,
  user,
  onAcknowledge
}) => {
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.employeeNo || user?.employeeNumber || user?.uid || '';

  const handleAcknowledge = async () => {
    if (!hasAgreed) {
      setError('Please check the agreement box to continue');
      return;
    }

    try {
      setIsAcknowledging(true);
      setError(null);

      const success = await changeLogService.acknowledgeChangeLog(
        userId,
        changeLog.id,
        changeLog.version
      );

      if (success) {
        // Small delay for better UX
        setTimeout(() => {
          onAcknowledge();
        }, 500);
      } else {
        setError('Failed to save acknowledgment. Please try again.');
      }
    } catch (err) {
      console.error('Error acknowledging change log:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsAcknowledging(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'new':
        return <SparklesIcon className={styles.categoryIcon} />;
      case 'improved':
        return <WrenchScrewdriverIcon className={styles.categoryIcon} />;
      case 'fixed':
        return <ShieldCheckIcon className={styles.categoryIcon} />;
      default:
        return <CheckCircleIcon className={styles.categoryIcon} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'new':
        return '#10b981'; // Green
      case 'improved':
        return '#3b82f6'; // Blue
      case 'fixed':
        return '#f59e0b'; // Orange
      default:
        return '#6b7280'; // Gray
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'new':
        return 'NEW';
      case 'improved':
        return 'IMPROVED';
      case 'fixed':
        return 'FIXED';
      default:
        return 'UPDATE';
    }
  };

  const daysRemaining = Math.ceil(
    (changeLog.expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Header with Celebration */}
        <div className={styles.modalHeader}>
          <div className={styles.celebrationBadge}>
            <SparklesIcon className={styles.celebrationIcon} />
            <span>NEW UPDATE</span>
          </div>
          
          <h1 className={styles.modalTitle}>
            What's New in Overtime Management
          </h1>
          
          <p className={styles.modalSubtitle}>
            Version {changeLog.version} • Released {changeLog.releaseDate.toLocaleDateString()}
          </p>

          {daysRemaining > 0 && (
            <div className={styles.expiryNotice}>
              This notice will be shown for the next {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Changes List */}
        <div className={styles.changesContainer}>
          <h2 className={styles.changesTitle}>Updates & Improvements</h2>
          
          <div className={styles.changesList}>
            {changeLog.changes.map((change, index) => (
              <div 
                key={index} 
                className={styles.changeItem}
                style={{ 
                  borderLeftColor: getCategoryColor(change.category),
                  background: `linear-gradient(135deg, ${getCategoryColor(change.category)}05, ${getCategoryColor(change.category)}02)`
                }}
              >
                <div className={styles.changeHeader}>
                  <div 
                    className={styles.categoryBadge}
                    style={{ 
                      background: getCategoryColor(change.category),
                      color: 'white'
                    }}
                  >
                    {getCategoryIcon(change.category)}
                    <span>{getCategoryLabel(change.category)}</span>
                  </div>
                  <h3 className={styles.changeTitle}>{change.title}</h3>
                </div>
                
                <p className={styles.changeDescription}>
                  {change.description}
                </p>

                {change.icon && (
                  <div className={styles.changeIconDisplay}>
                    {change.icon}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <div className={styles.importantNotice}>
          <ExclamationCircleIcon className={styles.noticeIcon} />
          <div>
            <strong>Important:</strong> These changes are now active in the overtime management system.
            Please review them carefully before creating new overtime requests.
          </div>
        </div>

        {/* Agreement Section */}
        <div className={styles.agreementSection}>
          <label className={styles.agreementLabel}>
            <input
              type="checkbox"
              checked={hasAgreed}
              onChange={(e) => {
                setHasAgreed(e.target.checked);
                if (e.target.checked) setError(null);
              }}
              className={styles.agreementCheckbox}
            />
            <span className={styles.agreementText}>
              I have read and understood the updates to the overtime management system
            </span>
          </label>

          {error && (
            <div className={styles.errorMessage}>
              <ExclamationCircleIcon className={styles.errorIcon} />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className={styles.modalFooter}>
          <button
            onClick={handleAcknowledge}
            disabled={!hasAgreed || isAcknowledging}
            className={styles.acknowledgeButton}
          >
            {isAcknowledging ? (
              <>
                <div className={styles.spinner}></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircleIcon className={styles.buttonIcon} />
                I Understand - Continue to Dashboard
              </>
            )}
          </button>

          <p className={styles.footerNote}>
            You'll only see this once. The changes are now part of your overtime workflow.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangeLogModal;